import { useState, useEffect, useMemo, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  listSmsNumbers,
  setSmsNumber,
  setOrgSmsRecovery,
  setClinicSmsRoutes,
  setClinicSmsQuietHours,
  type StaffOrg,
  type OrgSmsNumbers,
  type SmsRecoveryRoutes,
  type SmsQuietHours,
} from '../services/staff'
import { DEFAULT_TIMEZONE } from '../../shared/timezone'

interface SmsManagementProps {
  /** Org list, already loaded by StaffConsole (may be null while loading). */
  orgs: StaffOrg[] | null
  loadError: string | null
}

const RENDER_CAP = 50

/** The per-source route switches, in display order. */
const ROUTE_LABELS: ReadonlyArray<[keyof SmsRecoveryRoutes, string]> = [
  ['stedi', 'Medical (Stedi)'],
  ['vsp', 'VSP'],
  ['eyemed', 'EyeMed'],
  ['versant', 'Versant'],
]

/** A labelled on/off switch that saves on toggle. Reverts + toasts on error. */
function Toggle({ label, checked, disabled, onChange }: {
  label: string
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => Promise<void>
}) {
  const [saving, setSaving] = useState(false)
  async function flip() {
    setSaving(true)
    try {
      await onChange(!checked)
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }
  return (
    <label className={`sms-toggle${checked ? ' sms-toggle--on' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled || saving} onChange={flip} />
      <span>{label}</span>
    </label>
  )
}

/** The platform-default send window (mirrors SMS_QUIET_HOURS_* env defaults). */
const DEFAULT_WINDOW: SmsQuietHours = { startHour: 8, endHour: 18 }

const fmtHour = (h: number) => `${String(h).padStart(2, '0')}:00`

/**
 * Per-clinic SMS send-window editor. Explicit Save (start/end must be valid as
 * a pair); "Reset to default" clears the override so the platform default
 * (env-configured, 8–18) applies. Hours are clinic-local — the timezone shown
 * is what the sweeps evaluate the window in. Archived clinics are read-only
 * except clearing a leftover override.
 */
function QuietHoursRow({ value, timezone, archived, onSave }: {
  value: SmsQuietHours | null
  timezone: string | null
  archived: boolean
  onSave: (q: SmsQuietHours | null) => Promise<void>
}) {
  const [draft, setDraft] = useState<SmsQuietHours>(value ?? DEFAULT_WINDOW)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setDraft(value ?? DEFAULT_WINDOW) }, [value])
  const base = value ?? DEFAULT_WINDOW
  const changed = draft.startHour !== base.startHour || draft.endHour !== base.endHour
  const invalid = draft.startHour >= draft.endHour

  async function run(next: SmsQuietHours | null) {
    setSaving(true)
    try {
      await onSave(next)
      toast.success(next
        ? `SMS window saved: ${fmtHour(next.startHour)}–${fmtHour(next.endHour)}`
        : 'SMS window reset to default')
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? err.message : 'Could not save the SMS window.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="sms-mgmt-routes">
      <span className="sms-mgmt-routes__label">SMS window:</span>
      <select
        className="sms-mgmt-hours__select"
        value={draft.startHour}
        disabled={archived || saving}
        onChange={(e) => setDraft((d) => ({ ...d, startHour: Number(e.target.value) }))}
      >
        {Array.from({ length: 24 }, (_, h) => (
          <option key={h} value={h}>{fmtHour(h)}</option>
        ))}
      </select>
      <span className="sms-mgmt-routes__label">–</span>
      <select
        className="sms-mgmt-hours__select"
        value={draft.endHour}
        disabled={archived || saving}
        onChange={(e) => setDraft((d) => ({ ...d, endHour: Number(e.target.value) }))}
      >
        {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={h}>{fmtHour(h)}</option>
        ))}
      </select>
      <span className="sms-mgmt-routes__label">
        {value ? 'clinic override' : 'default'} · {timezone ?? DEFAULT_TIMEZONE}
      </span>
      {!archived && (
        <button
          className="staff-view-button"
          disabled={saving || !changed || invalid}
          title={invalid ? 'Start must be before end.' : undefined}
          onClick={() => run(draft)}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
      {value && (
        <button className="staff-view-button" disabled={saving} onClick={() => run(null)}>
          Reset to default
        </button>
      )}
    </div>
  )
}

/** One editable number row (org-wide or a clinic). Owns its own draft + save. */
function NumberRow({ label, sub, value, onSave, clearOnly }: {
  label: string
  sub?: string
  value: string | null
  onSave: (n: string) => Promise<string | null>
  /** Archived target: the existing number can only be cleared, not replaced. */
  clearOnly?: boolean
}) {
  const [draft, setDraft] = useState(value ?? '')
  const [saving, setSaving] = useState(false)
  useEffect(() => { setDraft(value ?? '') }, [value])
  const changed = draft.trim() !== (value ?? '')

  async function save() {
    setSaving(true)
    try {
      const stored = await onSave(clearOnly ? '' : draft.trim())
      setDraft(stored ?? '')
      toast.success(stored ? `Saved ${stored}` : 'Number cleared')
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? err.message : 'Could not save the number.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="sms-mgmt-row">
      <div className="sms-mgmt-row__label">
        <span className="sms-mgmt-row__name">{label}</span>
        {sub && <span className="sms-mgmt-row__sub">{sub}</span>}
      </div>
      <input
        className="sms-mgmt-row__input"
        type="tel"
        placeholder="+12065551234"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={saving || clearOnly}
      />
      <button
        className="staff-view-button"
        onClick={save}
        disabled={saving || (clearOnly ? !value : !changed)}
      >
        {saving ? 'Saving…' : !clearOnly && draft.trim() ? 'Save' : 'Clear'}
      </button>
    </div>
  )
}

/**
 * SMS Management — staff surface to map Telnyx numbers to orgs + clinics
 * (`integrations.telnyx.phoneNumber`), which is how patient SMS routes in and
 * out. Pick a practice, then set the org-wide safety-net number and/or a number
 * per clinic. Empty-then-Save clears a number. Archived clinics render disabled
 * — no new number can be assigned; one still holding a number offers Clear only,
 * so the number can be freed. The org-wide row is independent of clinic status.
 */
export default function SmsManagement({ orgs, loadError }: SmsManagementProps) {
  const [filter, setFilter] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [data, setData] = useState<OrgSmsNumbers | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Clinic whose route toggles are mid-save. Siblings are disabled meanwhile:
  // each save sends the WHOLE route map, so a second flip racing the first
  // would resend the first's render-time (stale) value and silently revert it.
  const [savingRoutesFor, setSavingRoutesFor] = useState<string | null>(null)

  const filtered = useMemo(() => {
    if (!orgs) return []
    const q = filter.trim().toLowerCase()
    const list = q
      ? orgs.filter((o) => o.name.toLowerCase().includes(q) || o.orgId.toLowerCase().includes(q))
      : orgs
    return list.slice(0, RENDER_CAP)
  }, [orgs, filter])

  const loadOrg = useCallback(async (orgId: string) => {
    setSelectedOrgId(orgId); setLoading(true); setError(null); setData(null)
    try {
      setData(await listSmsNumbers(orgId))
    } catch (err) {
      Sentry.captureException(err)
      setError(err instanceof Error ? err.message : 'Failed to load numbers.')
    } finally {
      setLoading(false)
    }
  }, [])

  // Returns the row-save handler for the org (clinicId null) or a clinic; folds
  // the stored value back into local state so the row reflects what was saved.
  const saveFor = useCallback(
    (clinicId: string | null) => async (n: string): Promise<string | null> => {
      const res = await setSmsNumber(selectedOrgId as string, clinicId, n)
      setData((prev) => {
        if (!prev) return prev
        if (clinicId === null) return { ...prev, orgNumber: res.phoneNumber }
        return {
          ...prev,
          clinics: prev.clinics.map((c) =>
            c.clinicId === clinicId ? { ...c, number: res.phoneNumber } : c),
        }
      })
      return res.phoneNumber
    },
    [selectedOrgId],
  )

  // ── Practice picker ──
  if (!selectedOrgId) {
    return (
      <div className="staff-emulator">
        <header className="staff-emulator__head">
          <div>
            <h1>SMS Management</h1>
            <p className="staff-console-subtitle">
              Assign Telnyx numbers to practices &amp; clinics. Inbound and outbound patient SMS route by these.
            </p>
          </div>
        </header>
        <div className="staff-console-toolbar">
          <input
            type="search"
            placeholder="Search practices by name or ID…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Search practices"
          />
        </div>
        {loadError && <p className="auth-error">{loadError}</p>}
        {!orgs && !loadError && <p className="staff-console-loading">Loading organizations…</p>}
        {orgs && (
          <ul className="staff-org-list">
            {filtered.map((org) => (
              <li key={org.orgId} className="staff-org-row">
                <div className="staff-org-meta">
                  <span className="staff-org-name">{org.name}</span>
                  <span className="staff-org-id">{org.orgId}</span>
                </div>
                <div className="staff-org-actions">
                  <button className="staff-view-button" onClick={() => loadOrg(org.orgId)}>Manage</button>
                </div>
              </li>
            ))}
            {filtered.length === 0 && <li className="staff-org-empty">No practices match “{filter}”.</li>}
          </ul>
        )}
      </div>
    )
  }

  // ── Org detail: number assignment ──
  return (
    <div className="staff-emulator">
      <header className="staff-emulator__head">
        <div>
          <button
            className="sms-mgmt-back"
            onClick={() => { setSelectedOrgId(null); setData(null); setError(null) }}
          >
            ← All practices
          </button>
          <h1>{data?.orgName ?? 'SMS Management'}</h1>
          <p className="staff-console-subtitle">
            One number per clinic; the org-wide number is the fallback when a clinic has none. Clear a field to unassign.
          </p>
        </div>
      </header>
      {loading && <p className="staff-console-loading">Loading numbers…</p>}
      {error && <p className="auth-error">{error}</p>}
      {data && (
        <div className="sms-mgmt-list">
          <div className="sms-mgmt-master">
            <Toggle
              label="SMS recovery"
              checked={data.smsRecoveryEnabled}
              onChange={async (next) => {
                const res = await setOrgSmsRecovery(selectedOrgId as string, next)
                setData((prev) => (prev ? { ...prev, smsRecoveryEnabled: res.enabled } : prev))
                toast.success(res.enabled ? 'SMS recovery on' : 'SMS recovery off')
              }}
            />
            <p className="sms-mgmt-hint">
              Master switch for this practice. When off, no failures are texted and the Recovery Loop
              page is hidden in the app. Eligibility checks still run either way.
            </p>
          </div>
          <div className="sms-mgmt-divider">Numbers</div>
          <NumberRow
            label="Org-wide (safety net)"
            sub="Used when a clinic has no number of its own"
            value={data.orgNumber}
            onSave={saveFor(null)}
          />
          <div className="sms-mgmt-divider">Clinics</div>
          {data.clinics.length === 0 && <p className="staff-org-empty">This practice has no clinics.</p>}
          {data.clinics.map((c) => (
            <div key={c.clinicId} className="sms-mgmt-clinic">
              <NumberRow
                label={c.name}
                sub={c.status !== 'active' ? c.status : undefined}
                value={c.number}
                onSave={saveFor(c.clinicId)}
                clearOnly={c.status === 'archived'}
              />
              <div className="sms-mgmt-routes">
                <span className="sms-mgmt-routes__label">SMS recovery sources:</span>
                {ROUTE_LABELS.map(([route, label]) => (
                  <Toggle
                    key={route}
                    label={label}
                    checked={c.smsRecoveryRoutes[route]}
                    // Archived clinics can't ENABLE a source (backend rejects
                    // it too), but a source already on can still be switched
                    // off. Siblings lock during a save — see savingRoutesFor.
                    disabled={
                      (c.status === 'archived' && !c.smsRecoveryRoutes[route]) ||
                      savingRoutesFor === c.clinicId
                    }
                    onChange={async (next) => {
                      setSavingRoutesFor(c.clinicId)
                      try {
                        const routes = { ...c.smsRecoveryRoutes, [route]: next }
                        const res = await setClinicSmsRoutes(selectedOrgId as string, c.clinicId, routes)
                        setData((prev) => prev ? {
                          ...prev,
                          clinics: prev.clinics.map((x) =>
                            x.clinicId === c.clinicId ? { ...x, smsRecoveryRoutes: res.routes } : x),
                        } : prev)
                      } finally {
                        setSavingRoutesFor(null)
                      }
                    }}
                  />
                ))}
              </div>
              <QuietHoursRow
                value={c.smsQuietHours}
                timezone={c.timezone}
                archived={c.status === 'archived'}
                onSave={async (q) => {
                  const res = await setClinicSmsQuietHours(selectedOrgId as string, c.clinicId, q)
                  setData((prev) => prev ? {
                    ...prev,
                    clinics: prev.clinics.map((x) =>
                      x.clinicId === c.clinicId ? { ...x, smsQuietHours: res.quietHours } : x),
                  } : prev)
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
